"use client";

import { TriangleAlert } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useEffect, useId, useMemo, useState } from "react";

import { WayflyerLogo } from "@/components/nav/main-nav";
import { Input } from "@/components/ui/input";
import { SliderRoot, SliderThumb } from "@/components/ui/slider";
import { financingOutcome } from "@/lib/bf-toolkit/calculations";
import { cn } from "@/lib/utils";

import { DEFAULTS, RANGES, STRINGS } from "./strings";

interface FinancingBreakEvenProps {
  /** Request locale, used only for number and currency formatting. */
  locale: string;
  /** ISO currency code for Intl formatting (e.g. "USD"). */
  currency: string;
  /** Currency symbol for the money input prefix (e.g. "$"). */
  currencySymbol: string;
}

// Fee slider curve. The track is non-linear so the low fees a founder actually
// cares about (0–20%) take ~80% of the travel, with the 20–30% tail
// compressed into the last stretch. `t` runs 0..1 across the slider.
const FEE_MAX = RANGES.fee.max;
const CURVE_K = 1.5;
const CURVE_DEN = Math.exp(CURVE_K) - 1;
const tToFee = (t: number) =>
  (FEE_MAX * (Math.exp(CURVE_K * t) - 1)) / CURVE_DEN;
const feeToT = (fee: number) =>
  Math.log((fee / FEE_MAX) * CURVE_DEN + 1) / CURVE_K;
const FEE_SLIDER_STEPS = 1000;

const clamp = (value: number, min: number, max: number) =>
  Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : min;

/**
 * Parse a number the way a founder might type it: strip currency symbols,
 * commas and stray characters, keep digits and a single decimal point. Returns
 * NaN for an empty/unparseable field so the UI can show a neutral prompt rather
 * than a bogus result.
 */
function parseNumber(raw: string): number {
  const cleaned = raw.replace(/[^0-9.]/g, "");
  if (cleaned === "" || cleaned === ".") return Number.NaN;
  const value = Number.parseFloat(cleaned);
  return Number.isFinite(value) ? value : Number.NaN;
}

/** Trim trailing zeros from a rounded number (2.00 → "2", 4.50 → "4.5"). */
function trim(n: number): string {
  return String(Number.parseFloat(n.toFixed(2)));
}

/** Format a whole number with thousands separators for a money field. The tool
 * is English-only, so grouping is always a comma. */
function groupNumber(n: number): string {
  return Math.round(n)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/** A sensible financed amount to prefill from a whole-order cost handed over by
 * another tool: about 90% of it, rounded to the nearest 5,000. */
function financeDefaultFor(orderCost: number): number {
  if (!Number.isFinite(orderCost) || orderCost <= 0) return 0;
  return Math.round((orderCost * 0.9) / 5000) * 5000;
}

export function FinancingBreakEven({
  locale,
  currency,
  currencySymbol,
}: FinancingBreakEvenProps) {
  const [marginStr, setMarginStr] = useState<string>(DEFAULTS.margin);
  const [priceStr, setPriceStr] = useState<string>(DEFAULTS.price);
  const [costStr, setCostStr] = useState<string>(DEFAULTS.cost);
  const [showPrices, setShowPrices] = useState<boolean>(false);
  const [financeStr, setFinanceStr] = useState<string>(DEFAULTS.finance);
  const [feeStr, setFeeStr] = useState<string>(String(DEFAULTS.fee));
  const [sellPct, setSellPct] = useState<number>(DEFAULTS.sell);

  const formatCurrency = useMemo(() => {
    const nf = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    });
    return (value: number) => nf.format(Math.round(value));
  }, [locale, currency]);

  // Prefill from a deep link (e.g. the order quantity calculator). Read once on
  // mount so the page itself stays static. Missing or invalid params fall back
  // to the defaults already in state.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const get = (key: string) => params.get(key);

    const price = get("price");
    const cost = get("cost");
    if (price !== null && cost !== null) {
      const p = parseNumber(price);
      const c = parseNumber(cost);
      if (Number.isFinite(p) && p > 0 && Number.isFinite(c)) {
        setShowPrices(true);
        setPriceStr(price);
        setCostStr(cost);
        setMarginStr(c < p ? trim(((p - c) / p) * 100) : "0");
      }
    } else {
      const margin = get("margin");
      if (margin !== null && Number.isFinite(parseNumber(margin))) {
        setMarginStr(margin);
      }
    }

    const finance = get("finance");
    const purchase = get("purchase");
    const purchaseNum = purchase !== null ? parseNumber(purchase) : Number.NaN;
    if (finance !== null && Number.isFinite(parseNumber(finance))) {
      setFinanceStr(groupNumber(parseNumber(finance)));
    } else if (Number.isFinite(purchaseNum)) {
      // `purchase` is a whole-order cost handed over by another tool (the order
      // quantity calculator links with it). This tool asks for the financed
      // amount only, so prefill the share a founder would typically finance and
      // let them adjust.
      setFinanceStr(groupNumber(financeDefaultFor(purchaseNum)));
    }
    const fee = get("fee");
    const feeNum = fee !== null ? parseNumber(fee) : Number.NaN;
    if (Number.isFinite(feeNum)) {
      // Clamped on the way in: the logic clamps to the slider's ceiling anyway,
      // so an out-of-range link would otherwise show a fee in the box that the
      // result below it never used.
      setFeeStr(trim(clamp(feeNum, RANGES.fee.min, RANGES.fee.max)));
    }
    const sell = get("sell");
    if (sell !== null && Number.isFinite(parseNumber(sell))) {
      setSellPct(clamp(parseNumber(sell), RANGES.sell.min, RANGES.sell.max));
    }
  }, []);

  // ----- margin <-> price/cost, kept in sync both ways -----
  function syncCostFromMargin(m: number) {
    if (!Number.isFinite(m)) return;
    let p = parseNumber(priceStr);
    if (!Number.isFinite(p) || p <= 0) {
      p = 100;
      setPriceStr("100");
    }
    setCostStr(trim(p * (1 - m / 100)));
  }
  function syncMarginFromPrices(pStr: string, cStr: string) {
    const p = parseNumber(pStr);
    const c = parseNumber(cStr);
    if (Number.isFinite(p) && p > 0 && Number.isFinite(c)) {
      setMarginStr(c < p ? trim(((p - c) / p) * 100) : "0");
    }
  }

  const margin = parseNumber(marginStr);
  const financed = parseNumber(financeStr);
  // Fee is held as a string so a founder can type a decimal point without it
  // collapsing; the logic and slider use the parsed, range-clamped value.
  const feePct = clamp(parseNumber(feeStr), RANGES.fee.min, RANGES.fee.max);

  const state = useMemo(() => {
    if (!Number.isFinite(margin) || margin <= 0 || margin >= 100) {
      return { kind: "empty" as const };
    }
    const amount = Number.isFinite(financed) && financed > 0 ? financed : 0;
    const outcome = financingOutcome({
      marginPct: margin,
      feePct,
      financedAmount: amount,
      sellThroughPct: sellPct,
    });
    const e = sellPct / 100;

    if (feePct <= 0) {
      return { kind: "free" as const, outcome, amount, e };
    }
    if (outcome.unrecoverable) {
      const best = financingOutcome({
        marginPct: margin,
        feePct,
        financedAmount: amount,
        sellThroughPct: 100,
      }).profitAtSellThrough;
      return { kind: "loss" as const, outcome, amount, e, best };
    }
    return { kind: "ok" as const, outcome, amount, e };
  }, [margin, financed, feePct, sellPct]);

  // Fire one analytics event ~800ms after the user stops adjusting, and only
  // when there's a real result. Raw money is intentionally not sent.
  const posthog = usePostHog();
  const resultKind = state.kind;
  const breakEven =
    state.kind === "ok" || state.kind === "loss" || state.kind === "free"
      ? state.outcome.breakEvenSellThrough
      : null;
  useEffect(() => {
    if (resultKind === "empty") return;
    const timer = setTimeout(() => {
      posthog?.capture("bf_tool_calculated", {
        tool: "financing-break-even",
        marginPct: Math.round(margin),
        feePct,
        sellPct,
        breakEvenSellThroughPct:
          breakEven === null || !Number.isFinite(breakEven)
            ? null
            : Math.round(breakEven * 1000) / 10,
        outcome: resultKind,
      });
    }, 800);
    return () => clearTimeout(timer);
  }, [posthog, resultKind, breakEven, margin, feePct, sellPct]);

  const isLoss = state.kind === "loss";

  return (
    <div className="border-grey-100 bg-surface relative rounded-2xl border p-5 sm:p-7">
      {/* Inputs */}
      <div className="flex flex-col gap-5">
        <h2 className="text-heading-16 font-semibold text-[#0D0D0D]">
          {STRINGS.toolTitle}
        </h2>

        {/* `sm:gap-y-8` only widens the one gap between the two input rows; the
            tighter gap-y-6 stays for the stacked single-column layout. */}
        <div className="grid gap-x-8 gap-y-6 sm:grid-cols-3 sm:gap-y-8">
          {/* Left third: the one amount, top-aligned so its label sits level
              with the gross margin slider beside it. */}
          <div>
            <MoneyField
              label={STRINGS.finance}
              value={financeStr}
              symbol={currencySymbol}
              note={STRINGS.financeNote}
              onChange={setFinanceStr}
            />
          </div>

          {/* Right two-thirds: gross margin, which opens the price/cost panel
              below itself. */}
          <div className="sm:col-span-2">
            <RangeField
              label={STRINGS.margin}
              min={RANGES.margin.min}
              max={RANGES.margin.max}
              step={RANGES.margin.step}
              sliderValue={clamp(margin, RANGES.margin.min, RANGES.margin.max)}
              onSlider={(v) => {
                setMarginStr(String(v));
                if (showPrices) syncCostFromMargin(v);
              }}
              numberValue={marginStr}
              onNumber={(v) => {
                setMarginStr(v);
                if (showPrices) syncCostFromMargin(parseNumber(v));
              }}
            >
              <button
                type="button"
                aria-expanded={showPrices}
                onClick={() => {
                  const next = !showPrices;
                  setShowPrices(next);
                  if (next) syncCostFromMargin(parseNumber(marginStr));
                }}
                className="text-body-14 text-pulse-700 mt-2 font-semibold text-pretty underline underline-offset-2"
              >
                {showPrices ? STRINGS.marginHelpClose : STRINGS.marginHelp}
              </button>
              {showPrices && (
                <div className="border-grey-100 mt-3 grid gap-3 rounded-xl border border-dashed p-4 sm:grid-cols-2">
                  <MoneyField
                    label={STRINGS.price}
                    value={priceStr}
                    symbol={currencySymbol}
                    onChange={(v) => {
                      setPriceStr(v);
                      syncMarginFromPrices(v, costStr);
                    }}
                  />
                  <MoneyField
                    label={STRINGS.cost}
                    value={costStr}
                    symbol={currencySymbol}
                    onChange={(v) => {
                      setCostStr(v);
                      syncMarginFromPrices(priceStr, v);
                    }}
                  />
                  <p className="text-body-14 text-text-label text-pretty sm:col-span-2">
                    {STRINGS.pricesNote}
                  </p>
                </div>
              )}
            </RangeField>
          </div>

          {/* Second row, spanning the full width so the fee starts flush with
              the amount field above it: the fee and expected sell-through side
              by side at equal width. */}
          <div className="grid gap-x-8 gap-y-6 sm:col-span-3 sm:grid-cols-2">
            <RangeField
              label={STRINGS.fee}
              note={STRINGS.feeNote}
              min={0}
              max={FEE_SLIDER_STEPS}
              step={1}
              sliderValue={Math.round(feeToT(feePct) * FEE_SLIDER_STEPS)}
              onSlider={(v) =>
                setFeeStr(
                  trim(Math.round(tToFee(v / FEE_SLIDER_STEPS) * 10) / 10)
                )
              }
              numberValue={feeStr}
              onNumber={setFeeStr}
              // Free typing while focused, then snap to the clamped value on the
              // way out. `feePct` is what the result below is computed from, so
              // the box can never be left showing a fee the maths ignored.
              onNumberBlur={() => setFeeStr(trim(feePct))}
              // The 20% label is left out: the curve puts it close enough to 30%
              // that the two collide at the widths this field renders at.
              ticks={[0, 5, 10, 30].map((fee) => ({
                pct: feeToT(fee) * 100,
                label: `${fee}%`,
              }))}
            />

            <RangeField
              label={STRINGS.sell}
              note={STRINGS.sellNote}
              min={RANGES.sell.min}
              max={RANGES.sell.max}
              step={RANGES.sell.step}
              sliderValue={sellPct}
              onSlider={setSellPct}
              numberValue={String(sellPct)}
              onNumber={(v) =>
                setSellPct(
                  clamp(parseNumber(v), RANGES.sell.min, RANGES.sell.max)
                )
              }
            />
          </div>
        </div>
      </div>

      {/* Result */}
      <div
        aria-live="polite"
        className={cn(
          "mt-6 rounded-2xl p-5 sm:p-7",
          isLoss ? "bg-red-50" : "bg-pulse-50"
        )}
      >
        {state.kind === "empty" && (
          <p className="text-body-16 text-text-label">{STRINGS.emptyPrompt}</p>
        )}

        {state.kind !== "empty" && (
          <Result
            state={state}
            sellPct={sellPct}
            feePct={feePct}
            marginPct={margin}
            formatCurrency={formatCurrency}
          />
        )}
      </div>

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <p className="text-body-14 text-text-label max-w-3xl italic">
          {STRINGS.disclaimer}
        </p>
        <WayflyerLogo
          aria-hidden="true"
          className="h-7 w-auto shrink-0 md:h-8 md:w-auto"
        />
      </div>
    </div>
  );
}

type ResultState =
  | { kind: "empty" }
  | {
      kind: "free";
      outcome: ReturnType<typeof financingOutcome>;
      amount: number;
      e: number;
    }
  | {
      kind: "loss";
      outcome: ReturnType<typeof financingOutcome>;
      amount: number;
      e: number;
      best: number;
    }
  | {
      kind: "ok";
      outcome: ReturnType<typeof financingOutcome>;
      amount: number;
      e: number;
    };

function Result({
  state,
  sellPct,
  feePct,
  marginPct,
  formatCurrency,
}: {
  state: Exclude<ResultState, { kind: "empty" }>;
  sellPct: number;
  feePct: number;
  marginPct: number;
  formatCurrency: (v: number) => string;
}) {
  const isLoss = state.kind === "loss";
  const y = state.outcome.breakEvenSellThrough;
  const yPct = Number.isFinite(y) ? Math.round(y * 1000) / 10 : 0;

  let eyebrow: string = STRINGS.eyebrow;
  // Always one decimal so the figure keeps a steady width as it ticks across
  // whole numbers (4.9 → 5.0 → 5.1), rather than jumping when a "5%" is shorter
  // than a "4.9%".
  let bignum = `${yPct.toFixed(1)}%`;
  if (state.kind === "free") {
    eyebrow = STRINGS.eyebrowFree;
    bignum = "0%";
  } else if (state.kind === "loss") {
    eyebrow = STRINGS.eyebrowLoss;
    bignum = ">100%";
  }

  return (
    <div>
      <p
        className={cn(
          "text-label-12 font-semibold uppercase",
          isLoss ? "text-red-700" : "text-pulse-700"
        )}
      >
        {eyebrow}
      </p>

      <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-7">
        <span
          className={cn(
            "font-extrabold tracking-tight tabular-nums sm:min-w-[3em] sm:text-left",
            isLoss
              ? "text-[2.75rem] leading-none text-red-700 lg:text-[3.75rem]"
              : "text-pulse-700 text-[3.25rem] leading-none lg:text-[4.75rem]"
          )}
        >
          {bignum}
        </span>
        <div>
          {state.kind === "loss" ? (
            <>
              <p className="text-heading-20 lg:text-heading-24 flex items-start gap-2 font-semibold text-balance text-red-700">
                <TriangleAlert
                  className="mt-1 size-5 shrink-0 text-red-600"
                  aria-hidden="true"
                />
                {STRINGS.leadLoss}
              </p>
              <p className="text-body-16 mt-2 text-pretty text-red-700">
                A {trim(feePct)}% fee on a {trim(marginPct)}% margin costs more
                than the margin it unlocks. Protect more margin or bring the fee
                down before financing this stock.
              </p>
            </>
          ) : state.kind === "free" ? (
            <p className="text-heading-20 lg:text-heading-24 font-semibold text-balance text-[#0D0D0D]">
              {STRINGS.leadFree}
            </p>
          ) : (
            <>
              <p className="text-heading-20 lg:text-heading-24 font-semibold text-balance text-[#0D0D0D]">
                {STRINGS.leadOk}
              </p>
              <p className="text-body-16 text-text-body mt-2 text-pretty">
                {state.e <= y ? (
                  <>
                    At the ~{sellPct}% you expect, you would be just under
                    break-even. A little more sell-through tips it into profit.
                  </>
                ) : (
                  <>
                    You expect to sell around{" "}
                    <span className="font-semibold text-[#0D0D0D]">
                      {sellPct}%
                    </span>
                    , roughly{" "}
                    <span className="font-semibold text-[#0D0D0D]">
                      {Math.round(state.e / y)}x
                    </span>{" "}
                    past break-even. Almost the whole order lands as profit on
                    top of the fee.
                  </>
                )}
              </p>
            </>
          )}
        </div>
      </div>

      <Runway
        yFrac={isLoss ? 1 : Number.isFinite(y) ? y : 0}
        eFrac={state.e}
        mode={state.kind}
        yLabel={bignum}
      />

      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
        {state.kind === "loss" ? (
          <LegendItem color="var(--red-600)" label={STRINGS.legendNever} />
        ) : (
          <>
            <LegendItem color="var(--red-600)" label={STRINGS.legendFee} />
            <LegendItem color="var(--pulse-700)" label={STRINGS.legendProfit} />
            <LegendItem
              color="var(--grey-100)"
              label={STRINGS.legendHeadroom}
            />
          </>
        )}
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        {state.kind === "loss" ? (
          <>
            <Tile
              label={STRINGS.tileFee}
              value={formatCurrency(state.outcome.feeAmount)}
              sub={`${trim(feePct)}% of ${formatCurrency(state.amount)}`}
            />
            <Tile
              label={STRINGS.tileBreakEven}
              value=">100%"
              sub="not reachable"
            />
            <Tile
              label={STRINGS.tileBest}
              value={formatCurrency(state.best)}
              sub="still short after the fee"
              tone={state.best < 0 ? "neg" : undefined}
            />
          </>
        ) : (
          <>
            <Tile
              label={STRINGS.tileFinanced}
              value={formatCurrency(state.amount)}
              sub={`${trim(feePct)}% fee applied`}
            />
            <Tile
              label={STRINGS.tileFee}
              value={formatCurrency(state.outcome.feeAmount)}
              sub={`${trim(feePct)}% total`}
            />
            <Tile
              label={`Profit at ~${sellPct}% sell-through`}
              value={`${state.outcome.profitAtSellThrough < 0 ? "" : "+"}${formatCurrency(state.outcome.profitAtSellThrough)}`}
              sub="after the fee"
              tone={state.outcome.profitAtSellThrough < 0 ? "neg" : "pos"}
            />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The break-even "runway": the whole 0–100% sell-through range with a red slice
 * that just pays the fee, a blue slice of profit up to what you expect, and the
 * faint headroom beyond. Break-even and expected sell-through are marked on the
 * true scale.
 */
function Runway({
  yFrac,
  eFrac,
  mode,
  yLabel,
}: {
  yFrac: number;
  eFrac: number;
  mode: "ok" | "free" | "loss";
  yLabel: string;
}) {
  const W = 720;
  const padX = 18;
  const trackY = 52;
  const trackH = 16;
  const H = 116;
  const plotW = W - 2 * padX;
  const x = (fr: number) => padX + clamp(fr, 0, 1) * plotW;
  const yDraw = Math.min(yFrac, 1);

  const bx = x(yDraw);
  const bAnchor = yDraw < 0.5 ? "start" : "end";
  const bdx = yDraw < 0.5 ? 6 : -6;
  const ex = x(eFrac);
  const eAnchor = eFrac < 0.5 ? "start" : "end";
  const edx = eFrac < 0.5 ? 6 : -6;

  return (
    <figure className="m-0 mt-6">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full"
        role="img"
        aria-label="Break-even against the full sell-through range."
      >
        <rect
          x={padX}
          y={trackY}
          width={plotW}
          height={trackH}
          rx={8}
          fill="var(--grey-100)"
        />
        {yDraw > 0 && (
          <rect
            x={x(0)}
            y={trackY}
            width={bx - x(0)}
            height={trackH}
            rx={8}
            fill="var(--red-600)"
          />
        )}
        {eFrac > yFrac && mode !== "loss" && (
          <rect
            x={bx}
            y={trackY}
            width={x(Math.min(eFrac, 1)) - bx}
            height={trackH}
            fill="var(--pulse-700)"
          />
        )}

        {/* break-even marker */}
        <line
          x1={bx}
          x2={bx}
          y1={trackY - 16}
          y2={trackY + trackH + 8}
          stroke="var(--grey-700)"
          strokeWidth={2}
        />
        <text
          x={bx + bdx}
          y={14}
          textAnchor={bAnchor}
          className="text-[11px] font-bold"
          fill="var(--grey-700)"
        >
          BREAK EVEN
        </text>
        <text
          x={bx + bdx}
          y={32}
          textAnchor={bAnchor}
          className="text-[18px] font-extrabold tabular-nums"
          fill="var(--label)"
        >
          {yLabel}
        </text>

        {/* expected marker */}
        {mode !== "loss" && (
          <>
            <line
              x1={ex}
              x2={ex}
              y1={trackY - 6}
              y2={trackY + trackH + 8}
              stroke="var(--blue-800)"
              strokeWidth={2}
              strokeDasharray="4 3"
            />
            <circle cx={ex} cy={trackY - 6} r={3} fill="var(--blue-800)" />
            <text
              x={ex + edx}
              y={trackY - 12}
              textAnchor={eAnchor}
              className="text-[11px] font-bold"
              fill="var(--blue-800)"
            >
              YOU EXPECT {Math.round(eFrac * 100)}%
            </text>
          </>
        )}

        {/* axis */}
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <g key={f}>
            <line
              x1={x(f)}
              x2={x(f)}
              y1={trackY + trackH + 4}
              y2={trackY + trackH + 9}
              stroke="var(--grey-400)"
              strokeWidth={1}
            />
            <text
              x={x(f)}
              y={trackY + trackH + 24}
              textAnchor="middle"
              className="text-[10px]"
              fill="var(--grey-400)"
            >
              {f * 100}%
            </text>
          </g>
        ))}
        <text
          x={W / 2}
          y={H - 6}
          textAnchor="middle"
          className="text-[11px]"
          fill="var(--body)"
        >
          {STRINGS.runwayAxis}
        </text>
      </svg>
    </figure>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <span className="text-label-12 text-text-body inline-flex items-center gap-1.5">
      <span
        aria-hidden="true"
        className="inline-block size-3 rounded-sm"
        style={{ background: color }}
      />
      {label}
    </span>
  );
}

function Tile({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  tone?: "pos" | "neg";
}) {
  return (
    <div className="bg-surface border-pulse-100 rounded-xl border p-4">
      <p className="text-label-11 text-grey-400 uppercase">{label}</p>
      <p
        className={cn(
          "mt-2 text-[1.6rem] leading-none font-bold tabular-nums",
          tone === "pos"
            ? "text-green-700"
            : tone === "neg"
              ? "text-red-700"
              : "text-[#0D0D0D]"
        )}
      >
        {value}
      </p>
      <p className="text-body-14 text-text-body mt-2">{sub}</p>
    </div>
  );
}

/**
 * A labelled control pairing a slider with a compact number box on one row, so
 * each input is narrow and the field is short. `sliderValue`/`onSlider` work in
 * the slider's own units (which, for the fee, is a curved 0..N scale), while
 * `numberValue`/`onNumber` carry the plain percentage the box shows.
 */
function RangeField({
  label,
  note,
  min,
  max,
  step,
  sliderValue,
  onSlider,
  numberValue,
  onNumber,
  onNumberBlur,
  ticks,
  className,
  children,
}: {
  label: string;
  note?: string;
  min: number;
  max: number;
  step: number;
  sliderValue: number;
  onSlider: (v: number) => void;
  numberValue: string;
  onNumber: (v: string) => void;
  /** Called when the box loses focus, for fields that hold a free-typed string
   * and need it normalised to the value the logic actually uses. Blur rather
   * than change, so a half-typed number isn't rewritten mid-keystroke. */
  onNumberBlur?: () => void;
  ticks?: { pct: number; label: string }[];
  className?: string;
  children?: React.ReactNode;
}) {
  const id = useId();
  return (
    <div className={className}>
      <label
        htmlFor={id}
        className="text-body-16 mb-2 block font-semibold text-[#0D0D0D]"
      >
        {label}
      </label>
      <div className="flex items-center gap-4">
        <div className="min-w-0 flex-1">
          <SliderRoot
            aria-label={label}
            min={min}
            max={max}
            step={step}
            value={[sliderValue]}
            onValueChange={(vals) => onSlider(vals[0] ?? min)}
            rangeClassName="bg-pulse-700"
            className="py-2"
          >
            <SliderThumb className="bg-pulse-700 focus-visible:ring-pulse-500 size-4 cursor-pointer rounded-full" />
          </SliderRoot>
          {ticks && (
            <div className="relative mt-1 h-4">
              {ticks.map((tick) => (
                <span
                  key={tick.label}
                  className={cn(
                    "text-label-11 text-grey-400 absolute tabular-nums",
                    // Keep the end ticks inside the track so the last one
                    // doesn't ride out over the number box beside it.
                    tick.pct <= 0
                      ? "translate-x-0"
                      : tick.pct >= 100
                        ? "-translate-x-full"
                        : "-translate-x-1/2"
                  )}
                  style={{ left: `${tick.pct}%` }}
                >
                  {tick.label}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="relative w-20 shrink-0">
          <Input
            id={id}
            inputMode="decimal"
            aria-label={label}
            value={numberValue}
            onChange={(e) => onNumber(e.target.value)}
            onBlur={onNumberBlur}
            className="text-heading-16 h-12 w-20 pr-6 text-right"
          />
          <span className="text-text-label pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2">
            %
          </span>
        </div>
      </div>
      {note && (
        <p className="text-body-14 text-text-label mt-2 text-pretty">{note}</p>
      )}
      {children}
    </div>
  );
}

function MoneyField({
  label,
  value,
  symbol,
  note,
  onChange,
}: {
  label: string;
  value: string;
  symbol: string;
  note?: string;
  onChange: (v: string) => void;
}) {
  const id = useId();
  return (
    <div>
      <label
        htmlFor={id}
        className="text-body-16 mb-2 block font-semibold text-[#0D0D0D]"
      >
        {label}
      </label>
      <div className="relative">
        <span className="text-text-label text-heading-16 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">
          {symbol}
        </span>
        <Input
          id={id}
          inputMode="decimal"
          aria-label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="text-heading-16 h-12 pl-8"
        />
      </div>
      {note && (
        <p className="text-body-14 text-text-label mt-1.5 text-pretty">
          {note}
        </p>
      )}
    </div>
  );
}
